import React from 'react'

import ListItem from '@mui/material/ListItem'
import ListItemAvatar from '@mui/material/ListItemAvatar'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemText from '@mui/material/ListItemText'
import Stack from '@mui/material/Stack'

import { CheckCircle, Cancel } from '@mxenabled/mx-icons'
import { InstitutionLogo, MerchantLogo, Text } from '@mxenabled/mxui'

import { useGlobalCopyStore, useRecurringTransactionsStore } from '../../common'
import { DATE_FORMATS_INTL, formatDate } from '../../core/constants'
import {
  type Recurrence,
  type RecurringCopy,
  RecurrenceStatus,
  RecurrenceType,
  RepeatingTransaction,
} from '../../core'

import { getFrequencyLabel } from '../../core/utils/RecurringTransactionsUtil'

import { IncomeBadge, MissedBadge, PaidBadge, UpcomingExpenseBadge } from './RecurrenceBadges'
import { formatCurrency } from '../../core/utils/NumberFormatting'
import { buildDynamicCopy } from '../../core/utils/Localization'

const getStatusIcon = (recurrence: Recurrence): React.ReactNode => {
  if (recurrence.type === RecurrenceType.Income) {
    return <IncomeBadge />
  }

  switch (recurrence.status) {
    case RecurrenceStatus.Missed:
      return <MissedBadge />
    case RecurrenceStatus.Paid:
      return <PaidBadge />
    default:
      return <UpcomingExpenseBadge />
  }
}

const getStatus = (recurrence: Recurrence, copy: RecurringCopy): string => {
  switch (recurrence.status) {
    case RecurrenceStatus.Paid:
      return recurrence.expectedDate
        ? (buildDynamicCopy(
            copy.paid_on,
            formatDate(
              recurrence.occurredOnDate || recurrence.expectedDate,
              DATE_FORMATS_INTL.MONTH_DAY,
            ),
          ) as string)
        : copy.paid
    case RecurrenceStatus.Missed:
      return buildDynamicCopy(
        copy.missed_on,
        formatDate(recurrence.expectedDate, DATE_FORMATS_INTL.MONTH_DAY),
      ) as string
    case RecurrenceStatus.Upcoming:
      return buildDynamicCopy(
        copy.upcoming_on,
        formatDate(recurrence.expectedDate, DATE_FORMATS_INTL.MONTH_DAY),
      ) as string
    default:
      return copy.unknown
  }
}

interface RecurrenceRowProps {
  recurrence: Recurrence
  isMini?: boolean
  onRecurrenceClick?: (recurrence: Recurrence) => void
  showOrdinal?: boolean
}

export const RecurrenceRow = ({
  recurrence,
  isMini = false,
  onRecurrenceClick = () => {},
  showOrdinal = false,
}: RecurrenceRowProps) => {
  const { recurring: copy } = useGlobalCopyStore()
  const { detailedRepeatingTransactions } = useRecurringTransactionsStore()

  const repeatingTransaction = React.useMemo(
    () =>
      detailedRepeatingTransactions.find((rt) => rt.guid === recurrence.repeatingTransactionGuid),
    [recurrence, detailedRepeatingTransactions],
  )

  const isCredit = recurrence.type === RecurrenceType.Income
  const isPaid = recurrence.status === RecurrenceStatus.Paid
  const isMissed = recurrence.status === RecurrenceStatus.Missed
  const isUpcoming = recurrence.status === RecurrenceStatus.Upcoming

  let statusLabel = copy.paid?.toLowerCase()
  if (isMissed) {
    statusLabel = copy.missed?.toLowerCase()
  } else if (isUpcoming) {
    statusLabel = showOrdinal
      ? getFrequencyLabel(copy, repeatingTransaction || ({} as RepeatingTransaction), true)
      : getFrequencyLabel(copy, repeatingTransaction || ({} as RepeatingTransaction), false)
  }

  const onItemClick = () => {
    onRecurrenceClick(recurrence)
  }

  return (
    //TODO: implement common/components/ListItemRow.tsx
    <ListItem disableGutters={true} disablePadding={true}>
      <ListItemButton onClick={onItemClick}>
        <ListItemAvatar>
          <MerchantLogo
            categoryGuid={recurrence.categoryGuid}
            merchantGuid={recurrence.merchantGuid || ''}
          />
        </ListItemAvatar>
        <ListItemText>
          <Stack
            direction="row"
            gap={4}
            sx={{ justifyContent: 'space-between', textOverflow: 'ellipsis', textWrap: 'nowrap' }}
          >
            <Stack direction="column" width="50%">
              <Text bold={true} variant="body1">
                {recurrence.name}
              </Text>
              <Stack alignItems="center" direction="row" gap={6}>
                {isMini ? (
                  <Stack direction="row" gap={4}>
                    {getStatusIcon(recurrence)}
                    <Text variant="caption">{getStatus(recurrence, copy)}</Text>
                  </Stack>
                ) : (
                  <>
                    {recurrence.institutionGuid && (
                      <InstitutionLogo
                        alt=""
                        institutionGuid={recurrence.institutionGuid}
                        size={12}
                      />
                    )}
                    <Text sx={{ maxWidth: 250 }} variant="caption">
                      {recurrence.institutionName}
                    </Text>
                  </>
                )}
              </Stack>
            </Stack>

            <Stack alignItems="flex-end" width="100%">
              <Text bold={true} color={isCredit ? 'success.main' : 'text.primary'} variant="body1">
                {isCredit ? '+' : ''}
                {formatCurrency(recurrence.amount, '0,0.00')}
              </Text>
              {!isMini && (
                <Stack
                  direction="row"
                  sx={{
                    alignItems: 'center',
                    gap: 6,
                    justifyContent: 'flex-end',
                    textTransform: 'uppercase',
                    width: '100%',
                  }}
                >
                  {isPaid && <CheckCircle color="success" filled={true} size={12} />}
                  {isMissed && <Cancel color="error" filled={true} size={12} />}
                  <Text bold={true} color="text.secondary" variant="caption">
                    {statusLabel}
                  </Text>
                </Stack>
              )}
            </Stack>
          </Stack>
        </ListItemText>
      </ListItemButton>
    </ListItem>
  )
}
